// Lógica pura de Segmentos: la lista, el borrador, la composición con cohorts y cómo se dice cada
// cosa. Nada de aquí toca la red ni React.
import { ApiError, userMessageOf } from '@/api/errors';
import {
    SEGMENT_ERROR_CODES,
    type SegmentErrorCode,
} from '@/api/services/loan-offerings/segments.contract';
import { classifyFailure } from '../creditFile/failures';
import { formatCount, formatPct } from '../cohorts/logic';
import { NOT_COUNTED_LABEL, type Cohort } from '../cohorts/types';
import {
    FALLBACK_COLOR_SLOT,
    MAX_SEGMENT_COHORTS,
    SEGMENT_COLOR_OPTIONS,
    SEGMENT_DESCRIPTION_MAX_LENGTH,
    SEGMENT_NAME_MAX_LENGTH,
    NEW_SEGMENT_NAME,
    type CustomerSegmentResult,
    type CustomerSegmentsTest,
    type RecountRun,
    type Segment,
    type SegmentCatalogState,
    type SegmentColorSlot,
    type SegmentDraft,
    type SegmentListFilter,
    type SegmentSort,
    type SegmentStatus,
    USAGE_STATUS_LABEL,
} from './types';

const collator = new Intl.Collator('es-MX', { sensitivity: 'base', numeric: true });

const COLOR_SLOTS: readonly SegmentColorSlot[] = SEGMENT_COLOR_OPTIONS.map((option) => option.slot);

/** Un `colorSlot` fuera de la paleta cae al gris: el chip se pinta igual, sin clase indefinida. */
export function toColorSlot(value: string | null | undefined): SegmentColorSlot {
    return COLOR_SLOTS.find((slot) => slot === value) ?? FALLBACK_COLOR_SLOT;
}

// ---------------------------------------------------------------------------------------------
// El modelo, dicho en palabras
// ---------------------------------------------------------------------------------------------

/** Lo mínimo para saber a quién alcanza un segmento: sus cohorts y su lista manual. */
export interface SegmentReach {
    cohortIds: readonly string[];
    memberIds: readonly string[];
}

/** A quién alcanza el segmento: los cohorts van con O y la lista manual entra siempre. */
export function membershipSentence(reach: SegmentReach): string {
    const cohortCount = reach.cohortIds.length;
    const memberCount = reach.memberIds.length;

    if (cohortCount === 0 && memberCount === 0) {
        return 'Todavía no alcanza a ningún cliente: no tiene cohorts ni clientes en la lista manual.';
    }
    if (cohortCount === 0) {
        return memberCount === 1
            ? 'Entra únicamente el cliente de la lista manual.'
            : `Entran únicamente los ${String(memberCount)} clientes de la lista manual.`;
    }

    const byCohort =
        cohortCount === 1
            ? 'Entra quien cumpla el cohort'
            : `Entra quien cumpla al menos uno de los ${String(cohortCount)} cohorts`;

    if (memberCount === 0) return `${byCohort}.`;

    const byMember =
        memberCount === 1
            ? 'más el cliente de la lista manual, cumpla o no un cohort'
            : `más los ${String(memberCount)} clientes de la lista manual, cumplan o no un cohort`;
    return `${byCohort}, ${byMember}.`;
}

const DEFAULT_MEMBERSHIP_SENTENCE =
    'Entra todo cliente que no esté en ningún otro segmento. La composición es automática.';

/** La frase de a quién alcanza, con la del catch-all aparte: su composición no se arma. */
export function segmentMembershipText(reach: SegmentReach, isDefault: boolean): string {
    return isDefault ? DEFAULT_MEMBERSHIP_SENTENCE : membershipSentence(reach);
}

/** Qué les pasa a los clientes al archivarlo: la pertenencia se evalúa al cotizar. */
export function archiveImpact(segment: Segment): string {
    return `Los clientes que hoy pertenecen a ${segment.name} dejan de pertenecer en cuanto se archiva. Los que no estén en otro segmento quedan en Estándar, que es el catch-all.`;
}

/** El conteo: «n · x %», o «Sin calcular»; con «(pendiente)» si quedó viejo. */
export function segmentCountText(
    segment: Pick<Segment, 'clientCount' | 'pctTotal'> & Partial<Pick<Segment, 'countPending'>>,
): string {
    const text = countText(segment.clientCount, segment.pctTotal);
    return segment.countPending === true ? `${text} (pendiente)` : text;
}

function countText(clientCount: number | null, pctTotal: number | null): string {
    if (clientCount === null) return NOT_COUNTED_LABEL;
    const count = formatCount(clientCount);
    return pctTotal === null ? count : `${count} · ${formatPct(pctTotal)}`;
}

/** El estado de una política o promoción en «Aplica en»; uno desconocido se muestra tal cual. */
export function usageStatusLabel(status: string): string {
    return Object.hasOwn(USAGE_STATUS_LABEL, status) ? USAGE_STATUS_LABEL[status] : status;
}

export interface SegmentCatalogInput {
    /** Si el rol tiene `loan.segment.read`. Sin él la lectura ni se dispara. */
    canRead: boolean;
    isLoading: boolean;
    error: Error | null;
}

/**
 * Cómo le llegó el catálogo a la pantalla que lo pide prestado. Sin permiso o con la lectura
 * caída el combo queda vacío, y un vacío mudo se lee como «este tenant no tiene segmentos».
 */
export function resolveSegmentCatalogState(input: SegmentCatalogInput): SegmentCatalogState {
    if (!input.canRead) return 'forbidden';
    if (input.isLoading) return 'loading';
    if (!input.error) return 'ready';
    return classifyFailure(input.error) === 'forbidden' ? 'forbidden' : 'unavailable';
}

/** Para qué se usa el catálogo en cada lugar. El mismo fallo estorba distinto en cada uno. */
export type SegmentCatalogPlace = 'filter' | 'picker';

const PURPOSE_BY_PLACE: Record<SegmentCatalogPlace, string> = {
    filter: 'para filtrar',
    picker: 'para elegir',
};

/** La frase que ve el operador cuando el catálogo no llegó. `undefined` cuando sí llegó. */
export function segmentCatalogNote(
    state: SegmentCatalogState,
    place: SegmentCatalogPlace,
): string | undefined {
    const purpose = PURPOSE_BY_PLACE[place];
    switch (state) {
        case 'ready':
            return undefined;
        case 'loading':
            return 'Leyendo el catálogo de segmentos del tenant…';
        case 'forbidden':
            return `Tu rol no incluye la lectura del catálogo de segmentos, así que no hay lista ${purpose}. Contacta a quien administra los roles.`;
        case 'unavailable':
            return `No se pudo leer el catálogo de segmentos: la lista ${purpose} puede estar incompleta o desactualizada. Vuelve a intentar; si sigue pasando, repórtalo.`;
    }
}

// ---------------------------------------------------------------------------------------------
// Composición con cohorts
// ---------------------------------------------------------------------------------------------

/** Una fila de «En este segmento»: el cohort, o el id de uno que ya no está en la lista. */
export interface SegmentCohortRow {
    cohortId: string;
    /** `null` = el cohort ya no existe para esta pantalla. */
    cohort: Cohort | null;
}

export interface SegmentCohortSplit {
    /** Cohorts activos que el segmento todavía no tiene. */
    available: Cohort[];
    /** Los del segmento, incluidos los archivados o desconocidos, para poder quitarlos. */
    inSegment: SegmentCohortRow[];
    /** Del total, no de lo que el buscador dejó ver. */
    availableCount: number;
    inSegmentCount: number;
}

function cohortMatches(cohort: Cohort, needle: string): boolean {
    if (needle === '') return true;
    return (
        cohort.name.toLowerCase().includes(needle) ||
        cohort.description.toLowerCase().includes(needle)
    );
}

/** Las dos columnas de la lista doble, cada una con su buscador. */
export function splitCohorts(
    cohorts: readonly Cohort[],
    cohortIds: readonly string[],
    availableQuery: string,
    inSegmentQuery: string,
): SegmentCohortSplit {
    const chosen = new Set(cohortIds);
    const availableAll = cohorts.filter(
        (cohort) => cohort.status === 'ACTIVE' && !chosen.has(cohort.cohortId),
    );
    const inSegmentAll = cohortIds.map((cohortId) => ({
        cohortId,
        cohort: cohorts.find((cohort) => cohort.cohortId === cohortId) ?? null,
    }));
    const availableNeedle = availableQuery.trim().toLowerCase();
    const inSegmentNeedle = inSegmentQuery.trim().toLowerCase();

    return {
        available: availableAll
            .filter((cohort) => cohortMatches(cohort, availableNeedle))
            .sort((a, b) => collator.compare(a.name, b.name)),
        inSegment: inSegmentAll.filter((row) =>
            row.cohort === null
                ? inSegmentNeedle === '' || row.cohortId.toLowerCase().includes(inSegmentNeedle)
                : cohortMatches(row.cohort, inSegmentNeedle),
        ),
        availableCount: availableAll.length,
        inSegmentCount: inSegmentAll.length,
    };
}

/** Mueve un cohort de un lado al otro de la lista doble. */
export function toggleCohortId(cohortIds: readonly string[], cohortId: string): string[] {
    return cohortIds.includes(cohortId)
        ? cohortIds.filter((id) => id !== cohortId)
        : [...cohortIds, cohortId];
}

/**
 * Los cohorts del borrador que el backend rechazaría (`SEGMENT_COHORT_UNKNOWN`): archivados o que
 * ya no están. Devuelve sus nombres, o el id si no hay nombre.
 */
export function unavailableCohorts(
    cohortIds: readonly string[],
    cohorts: readonly Cohort[],
): string[] {
    return cohortIds.flatMap((cohortId) => {
        const cohort = cohorts.find((item) => item.cohortId === cohortId);
        if (cohort === undefined) return [cohortId];
        return cohort.status === 'ACTIVE' ? [] : [cohort.name];
    });
}

// ---------------------------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------------------------

export function filterSegments(
    segments: readonly Segment[],
    query: string,
    status: SegmentListFilter,
): Segment[] {
    const needle = query.trim().toLowerCase();
    return segments.filter(
        (segment) =>
            segment.status === status &&
            (needle === '' ||
                segment.name.toLowerCase().includes(needle) ||
                segment.description.toLowerCase().includes(needle)),
    );
}

export function countByStatus(segments: readonly Segment[]): Record<SegmentListFilter, number> {
    const active = segments.filter((segment) => segment.status === 'ACTIVE').length;
    return { ACTIVE: active, ARCHIVED: segments.length - active };
}

/**
 * Ordena la lista dejando siempre el catch-all hasta arriba: es el ancla del modelo, no una fila
 * más. Por clientes, los que no se han contado van al final en cualquier dirección.
 */
export function sortSegments(segments: readonly Segment[], sort: SegmentSort): Segment[] {
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...segments].sort((a, b) => {
        if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
        switch (sort.key) {
            case 'cohorts':
                return (a.cohortIds.length - b.cohortIds.length) * factor;
            case 'members':
                return (a.memberIds.length - b.memberIds.length) * factor;
            case 'customers':
                if (a.clientCount === null || b.clientCount === null) {
                    return (a.clientCount === null ? 1 : 0) - (b.clientCount === null ? 1 : 0);
                }
                return (a.clientCount - b.clientCount) * factor;
            default:
                return collator.compare(a.name, b.name) * factor;
        }
    });
}

// ---------------------------------------------------------------------------------------------
// Borrador
// ---------------------------------------------------------------------------------------------

export function draftFromSegment(segment: Segment): SegmentDraft {
    return {
        name: segment.name,
        description: segment.description,
        colorSlot: segment.colorSlot,
        cohortIds: [...segment.cohortIds],
        memberIds: [...segment.memberIds],
    };
}

/** El borrador de un segmento nuevo. Estrena el primer color que nadie use, como el mock. */
export function emptyDraft(usedSlots: readonly string[]): SegmentDraft {
    const used = new Set(usedSlots);
    const free = COLOR_SLOTS.find((slot) => !used.has(slot)) ?? COLOR_SLOTS[0];
    return {
        name: NEW_SEGMENT_NAME,
        description: '',
        colorSlot: free,
        cohortIds: [],
        memberIds: [],
    };
}

/** Dos listas de ids son la misma aunque vengan en otro orden: mover un chip no es un cambio. */
export function sameIds(a: readonly string[], b: readonly string[]): boolean {
    if (a.length !== b.length) return false;
    const left = [...a].sort();
    const right = [...b].sort();
    return left.every((id, index) => id === right[index]);
}

/** Los campos que cambiaron respecto del original: los cinco que el PUT parcha. */
export function countDraftChanges(original: SegmentDraft, draft: SegmentDraft): number {
    let changes = 0;
    if (original.name !== draft.name) changes += 1;
    if (original.description !== draft.description) changes += 1;
    if (original.colorSlot !== draft.colorSlot) changes += 1;
    if (!sameIds(original.cohortIds, draft.cohortIds)) changes += 1;
    if (!sameIds(original.memberIds, draft.memberIds)) changes += 1;
    return changes;
}

/** Si la composición cambió: el conteo queda pendiente al guardar. */
export function compositionChanged(original: SegmentDraft, draft: SegmentDraft): boolean {
    return (
        !sameIds(original.cohortIds, draft.cohortIds) ||
        !sameIds(original.memberIds, draft.memberIds)
    );
}

/** Lo mismo guardado, sin contar espacios al borde: así compara el editor contra el servidor. */
export function sameSegmentContent(a: SegmentDraft, b: SegmentDraft): boolean {
    return (
        a.name.trim() === b.name.trim() &&
        a.description.trim() === b.description.trim() &&
        a.colorSlot === b.colorSlot &&
        sameIds(a.cohortIds, b.cohortIds) &&
        sameIds(a.memberIds, b.memberIds)
    );
}

export interface DraftCheck {
    isDefault: boolean;
    /** Lo guardado; `null` en un alta. */
    original: SegmentDraft | null;
    /** Nombres de los cohorts del borrador que ya no se pueden mandar. */
    unavailableCohorts: readonly string[];
}

/** Si el guardado manda las listas: el alta siempre; una edición, si cambió alguna. */
export function touchesLists(original: SegmentDraft | null, draft: SegmentDraft): boolean {
    return original === null || compositionChanged(original, draft);
}

/** Lo que la pantalla corta antes de que el backend responda 400 o 422. */
export function validateDraft(draft: SegmentDraft, check: DraftCheck): string[] {
    if (check.isDefault && (draft.cohortIds.length > 0 || draft.memberIds.length > 0)) {
        return [
            'El segmento Estándar es catch-all: no acepta cohorts ni individuos, solo cambio de nombre.',
        ];
    }

    const problems: string[] = [];
    const name = draft.name.trim();
    if (name === '') problems.push('Ponle un nombre al segmento.');
    else if (name.length > SEGMENT_NAME_MAX_LENGTH) {
        problems.push(`El nombre no puede pasar de ${String(SEGMENT_NAME_MAX_LENGTH)} caracteres.`);
    }
    if (draft.description.length > SEGMENT_DESCRIPTION_MAX_LENGTH) {
        problems.push(
            `La descripción no puede pasar de ${String(SEGMENT_DESCRIPTION_MAX_LENGTH)} caracteres.`,
        );
    }
    // Como el backend: la composición solo se revisa si el parche toca las listas.
    if (check.isDefault || !touchesLists(check.original, draft)) return problems;
    if (draft.cohortIds.length === 0 && draft.memberIds.length === 0) {
        problems.push('Agrega al menos un cohort o un cliente a la lista manual.');
    }
    if (draft.cohortIds.length > MAX_SEGMENT_COHORTS) {
        problems.push(`Un segmento admite hasta ${String(MAX_SEGMENT_COHORTS)} cohorts.`);
    }
    // Los cohorts solo viajan si cambiaron: solo entonces importa que sigan activos.
    const sendsCohorts =
        check.original === null || !sameIds(check.original.cohortIds, draft.cohortIds);
    if (sendsCohorts && check.unavailableCohorts.length > 0) {
        problems.push(
            `Quita los cohorts archivados o que ya no existen: ${check.unavailableCohorts.join(', ')}.`,
        );
    }
    return problems;
}

// ---------------------------------------------------------------------------------------------
// Recálculo
// ---------------------------------------------------------------------------------------------

/** La última corrida que terminó bien: de ahí sale «Última evaluación». */
export function lastFinishedRun(runs: readonly RecountRun[]): RecountRun | null {
    return runs.find((run) => run.status === 'FINISHED') ?? null;
}

/** «1 min 20 s», «45 s», o «—» sin dato. */
export function formatDuration(durationMs: number | null): string {
    if (durationMs === null) return '—';
    const seconds = Math.round(durationMs / 1000);
    if (seconds < 60) return `${String(seconds)} s`;
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return rest === 0 ? `${String(minutes)} min` : `${String(minutes)} min ${String(rest)} s`;
}

// ---------------------------------------------------------------------------------------------
// ¿En qué segmentos cae este cliente?
// ---------------------------------------------------------------------------------------------

/** Por qué entra o no a un segmento, con los nombres de los cohorts que cumplió. */
export function membershipReasonText(
    result: CustomerSegmentResult,
    cohortNames: ReadonlyMap<string, string>,
): string {
    if (!result.matched) return 'no entra';
    if (result.reason === 'MEMBER') return 'entra por la lista manual';
    const names = result.matchedCohortIds.map((cohortId) => cohortNames.get(cohortId) ?? cohortId);
    if (names.length === 0) return 'entra por un cohort';
    return names.length === 1
        ? `entra por el cohort ${names[0]}`
        : `entra por los cohorts ${names.join(', ')}`;
}

/**
 * Si de este segmento no se puede decir que no entra: los cohorts no se evaluaron y el segmento
 * usa alguno, o no se sabe su composición. Lo que entra por la lista manual sí cuenta.
 */
export function cohortsLeftUnevaluated(
    test: CustomerSegmentsTest,
    result: CustomerSegmentResult,
    cohortIdsBySegment: ReadonlyMap<string, readonly string[]>,
): boolean {
    if (test.evaluatorAvailable || result.matched) return false;
    const cohortIds = cohortIdsBySegment.get(result.segmentId);
    return cohortIds === undefined || cohortIds.length > 0;
}

// ---------------------------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------------------------

/**
 * Cada `code` del contrato con su mensaje y con lo que el operador tiene que hacer. Lo que no
 * esté aquí cae a `userMessageOf`, nunca a un `error.message` crudo.
 */
const MESSAGE_BY_SEGMENT_CODE: Record<SegmentErrorCode, string> = {
    SEGMENT_NOT_FOUND: 'Ese segmento ya no existe. Refresca la lista y vuelve a intentarlo.',
    SEGMENT_NAME_TAKEN: 'Ya hay otro segmento con ese nombre. Ponle uno distinto.',
    SEGMENT_VERSION_CONFLICT:
        'Alguien más cambió este segmento; recarga para ver la versión actual.',
    DEFAULT_SEGMENT_NOT_EDITABLE:
        'El segmento Estándar solo acepta cambio de nombre: es el catch-all y su composición es automática.',
    SEGMENT_IN_USE:
        'Hay políticas o promociones vivas apuntando a este segmento. Reasígnalas antes de archivarlo.',
    SEGMENT_COHORT_UNKNOWN:
        'Uno de los cohorts ya no existe o está archivado. Recarga la pantalla y quítalo del segmento.',
    SEGMENT_EMPTY:
        'El segmento necesita al menos un cohort activo o un cliente en la lista manual.',
    RECOUNT_IN_PROGRESS: 'Ya hay un recálculo en curso.',
    RECOUNT_DISPATCH_FAILED: 'No se pudo iniciar el recálculo. Intenta de nuevo en un momento.',
    LOAN_CREDIT_UNAVAILABLE:
        'No se pudieron leer los datos del cliente. Intenta de nuevo en un momento.',
};

/** Si el `code` que llegó es uno del contrato, y no cualquier string del backend. */
function isSegmentErrorCode(code: string): code is SegmentErrorCode {
    return (SEGMENT_ERROR_CODES as readonly string[]).includes(code);
}

/** El mensaje que se le enseña al operador. Nunca el `message` crudo de un error de programación. */
export function segmentErrorCopy(error: unknown): string {
    if (error instanceof ApiError && isSegmentErrorCode(error.code)) {
        return MESSAGE_BY_SEGMENT_CODE[error.code];
    }
    return userMessageOf(error);
}

/** Archivar o reactivar: al reactivar, quedarse sin composición se arregla antes de reintentar. */
export function statusErrorCopy(error: unknown, to: SegmentStatus): string {
    if (to === 'ACTIVE' && error instanceof ApiError && error.code === 'SEGMENT_EMPTY') {
        return 'No se puede reactivar: no le quedan cohorts activos ni clientes en la lista manual. Reactiva alguno de sus cohorts o cambia su composición antes.';
    }
    return segmentErrorCopy(error);
}
